// Description: Sales totals, top sellers and sales by employee for a date range

import React, { useContext, useEffect, useState } from 'react'
import { Container, Table, Row, Col, Button, Form } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Loader from '../components/Loader'
import Message from '../components/Message'
import { getSalesReport } from '../actions/reportActions'
import { ThemeContext } from '../components/ThemeContext.js'
import { formatCurrency } from '../utils/cart.js'
import {
  EMPLOYEE_PERMISSIONS,
  hasEmployeePermission
} from '../constants/employeePermissions.js'

const SalesReport = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [group, setGroup] = useState('day')

  const { loading, error, report } = useSelector((state) => state.salesReport)
  const { userInfo } = useSelector((state) => state.userLogin)
  const { ownerInfo } = useSelector((state) => state.ownerLogin)

  const canViewReports = hasEmployeePermission(
    userInfo,
    ownerInfo,
    EMPLOYEE_PERMISSIONS.REPORTS_VIEW
  )

  useEffect(() => {
    if (!userInfo && !ownerInfo) {
      navigate('/login')
    } else if (!canViewReports) {
      navigate('/pos')
    } else {
      dispatch(getSalesReport({ from, to, group }))
    }
  }, [dispatch, navigate, userInfo, ownerInfo, canViewReports, from, to, group])

  const totals = report?.totals

  return (
    <Container
      className='my-3 report-page report-print-area'
      style={{ color: theme.text }}
    >
      <Row className='align-items-center report-page-heading'>
        <Col xs={12} md>
          <h1>Sales summary</h1>
          <p>Review sales activity, revenue, and top sellers.</p>
        </Col>
        <Col xs={12} md='auto' className='text-md-end report-no-print'>
          <Button
            size='sm'
            className='my-3 me-2'
            variant='outline-primary'
            onClick={() => window.print()}
          >
            Print Report
          </Button>
          <Button
            size='sm'
            className='my-3'
            variant='outline-primary'
            onClick={() => navigate('/reports')}
          >
            Go Back
          </Button>
        </Col>
      </Row>

      <Form className='report-filter-panel d-flex flex-wrap align-items-end report-no-print'>
        <Form.Group>
          <Form.Label className='me-2 mb-0'>From</Form.Label>
          <Form.Control
            type='date'
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className='me-2 mb-0'>To</Form.Label>
          <Form.Control
            type='date'
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className='me-2 mb-0'>Group by</Form.Label>
          <Form.Control
            as='select'
            value={group}
            onChange={(e) => setGroup(e.target.value)}
          >
            <option value='day'>By day</option>
            <option value='month'>By month</option>
          </Form.Control>
        </Form.Group>
        <small className='report-filter-hint'>
          Leave dates empty for the last 30 days. Figures are net of returns.
        </small>
      </Form>

      {loading && <Loader />}
      {error && <Message variant='danger'>{error}</Message>}

      {report && totals && (
        <>
          <Table
            striped
            bordered
            size='sm'
            className='my-3 report-table report-summary-table'
          >
            <tbody>
              <tr>
                <th>Sales</th>
                <td>{totals.sales}</td>
              </tr>
              <tr>
                <th>Subtotal</th>
                <td>{formatCurrency(totals.subtotal)}</td>
              </tr>
              <tr>
                <th>Discounts given</th>
                <td>{formatCurrency(totals.discount)}</td>
              </tr>
              <tr>
                <th>Shipping</th>
                <td>{formatCurrency(totals.shipping)}</td>
              </tr>
              <tr>
                <th>Tax collected</th>
                <td>{formatCurrency(totals.taxes)}</td>
              </tr>
              <tr>
                <th>Total</th>
                <td>{formatCurrency(totals.total)}</td>
              </tr>
              <tr>
                <th>Fully returned sales</th>
                <td>{report.returnedCount}</td>
              </tr>
              <tr>
                <th>Partial returns</th>
                <td>{report.partialReturnCount}</td>
              </tr>
              <tr>
                <th>Refunded</th>
                <td>{formatCurrency(totals.refunds)}</td>
              </tr>
            </tbody>
          </Table>

          <h5>{group === 'month' ? 'Monthly' : 'Daily'} totals</h5>
          <Table striped bordered size='sm' className='report-table'>
            <thead>
              <tr>
                <th>Period</th>
                <th>Sales</th>
                <th>Tax</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {report.periods.map((p) => (
                <tr key={p.period}>
                  <td>{p.period}</td>
                  <td>{p.sales}</td>
                  <td>{formatCurrency(p.taxes)}</td>
                  <td>{formatCurrency(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <h5>Top sellers</h5>
          <Table striped bordered size='sm' className='report-table'>
            <thead>
              <tr>
                <th>UPC</th>
                <th>Item</th>
                <th>Manufacturer</th>
                <th>Name</th>
                <th>Qty</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {report.topItems.map((i) => (
                <tr key={i.inventory}>
                  <td>{i.upc}</td>
                  <td>{i.item}</td>
                  <td>{i.manufacturer}</td>
                  <td>{i.subname}</td>
                  <td>{i.quantity}</td>
                  <td>{formatCurrency(i.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <h5>Sales by employee</h5>
          <Table striped bordered size='sm' className='report-table'>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Sales</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {report.byEmployee.map((e) => (
                <tr key={e.name}>
                  <td>{e.name}</td>
                  <td>{e.sales}</td>
                  <td>{formatCurrency(e.total)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  )
}

export default SalesReport
